import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/base/SchemaOrg';
import { hotMovies, latestMovies, articleContent, type MovieItem } from '@/mocks/movies';

const allMovies: MovieItem[] = [...hotMovies, ...latestMovies];

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const movie = allMovies.find((m) => m.id === Number(id));

  if (!movie) {
    const notFoundSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      'name': '影片不存在 - 四虎影院',
      'url': `https://realsmodel.cn/article/${id}`,
      'inLanguage': 'zh-CN',
    };
    return (
      <>
        <SchemaOrg data={notFoundSchema} />
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-semibold text-foreground-950 mb-2">影片不存在</h2>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const details = articleContent[movie.id] || {
    synopsis: movie.description || '暂无简介',
    director: '未知',
    cast: '未知',
    episodes: '1集全',
    region: '未知',
    language: '未知',
    releaseDate: `${movie.year}`,
  };

  const relatedMovies = allMovies
    .filter((m) => m.category === movie.category && m.id !== movie.id)
    .slice(0, 6);

  const categorySlugMap: Record<string, string> = {
    '电影': 'movie', '科幻': 'movie', '悬疑': 'movie', '犯罪': 'movie', '战争': 'movie', '动作': 'movie', '奇幻': 'movie',
    '电视剧': 'tv',
    '综艺': 'variety',
    '动画': 'anime', '动漫': 'anime',
  };
  const categorySlug = categorySlugMap[movie.category] || 'movie';

  const articleSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': `${movie.title} - 四虎影院`,
    'description': details.synopsis?.substring(0, 150) || movie.title,
    'image': movie.image,
    'datePublished': details.releaseDate,
    'author': { '@type': 'Organization', 'name': '四虎影院' },
    'publisher': { '@type': 'Organization', 'name': '四虎影院', 'url': 'https://realsmodel.cn/' },
    'url': `https://realsmodel.cn/article/${movie.id}`,
    'inLanguage': 'zh-CN',
  }), [movie, details]);

  const breadcrumbSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'https://realsmodel.cn/' },
      { '@type': 'ListItem', 'position': 2, 'name': movie.category, 'item': `https://realsmodel.cn/category/${categorySlug}` },
      { '@type': 'ListItem', 'position': 3, 'name': movie.title, 'item': `https://realsmodel.cn/article/${movie.id}` },
    ],
  }), [movie, categorySlug]);

  return (
    <>
      <SchemaOrg data={articleSchema} />
      <SchemaOrg data={breadcrumbSchema} />
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen">
        <div className="bg-background-950 pt-20 pb-0">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 pt-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-6">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <span>/</span>
              <Link to={`/category/${categorySlug}`} className="hover:text-primary-500 transition-colors">{movie.category}</Link>
              <span>/</span>
              <span className="text-foreground-200">{movie.title}</span>
            </nav>
          </div>

          <div className="max-w-[1400px] mx-auto px-4 md:px-6 pb-10">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="shrink-0 w-full lg:w-[320px]">
                <div className="relative aspect-[2/3] rounded-xl overflow-hidden">
                  <img
                    src={movie.image}
                    alt={movie.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-3 right-3 px-3 py-1 bg-black/60 backdrop-blur-sm rounded-lg text-sm text-yellow-400 font-bold">
                    {movie.rating}
                  </div>
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-50 mb-3">
                  {movie.title}
                </h1>

                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  {movie.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-primary-500/20 text-primary-300 whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                  <span className="text-xs text-foreground-400">{movie.updateInfo}</span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                  {[
                    { label: '导演', value: details.director },
                    { label: '主演', value: details.cast },
                    { label: '类型', value: movie.category },
                    { label: '地区', value: details.region },
                    { label: '语言', value: details.language },
                    { label: '上映', value: details.releaseDate },
                    { label: '集数', value: details.episodes },
                    { label: '年份', value: `${movie.year}` },
                  ].map((item) => (
                    <div key={item.label} className="text-sm">
                      <span className="text-foreground-500">{item.label}：</span>
                      <span className="text-foreground-200">{item.value}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-3">
                  <button className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer">
                    <i className="ri-play-circle-line text-lg"></i>
                    立即播放
                  </button>
                  <button className="inline-flex items-center gap-2 px-6 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-200 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer">
                    <i className="ri-heart-line"></i>
                    收藏
                  </button>
                  <button className="inline-flex items-center gap-2 px-6 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-200 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer">
                    <i className="ri-share-line"></i>
                    分享
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-10">
          <div className="mb-10">
            <h2 className="text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-article-line text-primary-500"></i>
              剧情简介
            </h2>
            <p className="text-sm text-foreground-600 leading-relaxed max-w-3xl">
              {details.synopsis}
            </p>
          </div>

          {relatedMovies.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-foreground-950 flex items-center gap-2">
                  <i className="ri-movie-line text-primary-500"></i>
                  相关推荐
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {relatedMovies.map((m) => (
                  <Link
                    key={m.id}
                    to={`/article/${m.id}`}
                    className="group block cursor-pointer"
                  >
                    <div className="relative aspect-[2/3] rounded-lg overflow-hidden mb-3">
                      <img
                        src={m.image}
                        alt={m.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute top-2 right-2 px-2 py-0.5 bg-black/60 backdrop-blur-sm rounded text-xs text-yellow-400 font-semibold">
                        {m.rating}
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-950 line-clamp-1 group-hover:text-primary-500 transition-colors">
                      {m.title}
                    </h3>
                    <p className="text-xs text-foreground-400 mt-0.5">{m.category}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}